import React, { useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import moment from 'moment';

import { MODALITY_OPTIONS_LABELS, TYPE_OPTIONS_ENUM, observationColors, observationStatuses } from '../../constants/observations';
import CaseStatusCell from '../cases/CaseStatusCell';
import TypeChip from '../common/TypeChip';

const useStyles = makeStyles((theme) => ({
  bottomRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  detailItem: {
    display: 'flex',
    marginRight: 24,
  },
  detailLabel: {
    color: '#999',
    fontSize: 14,
  },
  detailValue: {
    color: '#172B4D',
    fontSize: 14,
    marginLeft: 5,
    marginRight: 24,
  },
  detailsRow: {
    display: 'flex',
    marginTop: 4,
  },
  examType: {
    marginLeft: 4,
  },
  facilityName: {
    color: '#2196f3',
    fontSize: 16,
    fontWeight: 700,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  holdReason: {
    alignItems: 'center',
    backgroundColor: '#F7F7F7',
    borderRadius: 5,
    color: '#777',
    display: 'flex',
    fontSize: 14,
    marginTop: 16,
    padding: '16px 20px',
    textAlign: 'left',
  },
  newBadge: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    color: 'white',
    display: 'flex',
    fontSize: 10,
    gap: 4,
    marginLeft: 18,
    padding: '5px 8px',
  },
  newBadgeDot: {
    backgroundColor: 'white',
    borderRadius: '50%',
    display: 'inline-flex',
    height: 6,
    width: 6,
  },
  patientInfo: {
    alignItems: 'center',
    display: 'flex',
  },
  patientName: {
    color: '#333',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 600,
  },
  seeHoldReason: {
    alignItems: 'center',
    color: '#172B4D',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14,
    gap: 4,
  },
  timestamp: {
    color: '#999',
    fontSize: 14,
  },
  worklistItem: {
    '&:hover': {
      border: '2px solid #2AAAE2 !important',
      cursor: 'pointer',
      padding: '15px 19px',
    },
    border: '1px solid #DEDEDE',
    borderRadius: 5,
    fontFamily: 'Roboto',
    padding: '16px 20px',
  },
}));

const WorklistItem = ({ item, onClick, isNew = false }) => {
  const classes = useStyles();
  const [showHoldReason, setShowHoldReason] = useState(item.status === observationStatuses.on_hold.value);

  const formattedDate = item.createdAt ? moment(item.createdAt).format('MM/DD/YYYY hh:mm A') : '';

  const handleOpenHoldReason = (event) => {
    event.stopPropagation();
    setShowHoldReason(!showHoldReason);
  };

  let statStyle = { border: '', padding: '' };

  if (item.status === observationStatuses.new.value && item.type === TYPE_OPTIONS_ENUM.STAT) {
    statStyle = { border: '2px solid red', padding: '15px 19px' };
  }

  return (
    <Box className={classes.worklistItem} style={statStyle} onClick={() => onClick(item)} title="Open Case">
      <Box className={classes.header}>
        <Box className={classes.patientInfo} style={{ gap: 10 }}>
          {item.type && <TypeChip type={item.type} size="small" />}

          <CaseStatusCell color={observationColors[item.status]} blackFont={item.status === observationStatuses.on_hold.value} status={observationStatuses[item.status]?.label} />

          <Typography className={classes.timestamp}>{formattedDate}</Typography>
        </Box>

        {item.facility?.name && <Typography className={classes.facilityName}>{item.facility.name}</Typography>}
      </Box>

      <Box className={classes.detailsRow}>
        <Typography className={classes.patientName}>
          {item.patientName || 'Unknown Patient'}
          {item.examName && <span className={classes.examType}>, {item.examName}</span>}
        </Typography>

        {isNew && (
          <Box className={classes.newBadge}>
            <span className={classes.newBadgeDot} />
            <span>NEW!</span>
          </Box>
        )}
      </Box>

      <Box className={classes.bottomRow}>
        <Box className={classes.detailsRow}>
          <Box className={classes.detailItem}>
            <Typography className={classes.detailLabel}>Department:</Typography>
            <Typography className={classes.detailValue}>{item.department?.name || 'N/A'}</Typography>
          </Box>

          <Box className={classes.detailItem}>
            <Typography className={classes.detailLabel}>Modality:</Typography>
            <Typography className={classes.detailValue}>{MODALITY_OPTIONS_LABELS[item.modality] || 'N/A'}</Typography>
          </Box>

          <Box className={classes.detailItem}>
            <Typography className={classes.detailLabel}>Radiologist:</Typography>
            <Typography className={classes.detailValue} style={{ fontWeight: 'bold', marginRight: 0 }}>
              {item.radiologist || 'N/A'}
            </Typography>
          </Box>
        </Box>

        {item.onHoldReason && item.status === observationStatuses.on_hold.value && (
          <Box className={classes.seeHoldReason} title="See On Hold Message" onClick={handleOpenHoldReason}>
            On Hold Message {showHoldReason ? <ExpandLess /> : <ExpandMore />}
          </Box>
        )}
      </Box>

      {showHoldReason && <Box className={classes.holdReason}>{item.onHoldReason}</Box>}
    </Box>
  );
};

export default WorklistItem;
